import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Link, Font } from '@react-pdf/renderer';
import { Descendant, Text as SlateText, Element as SlateElement } from 'slate';
import { watermarkForOnePager } from '../../../assets/hostedassets';

// Convert browser pixels (96 DPI) to PDF points (72 DPI)
const pxToPt = (px: number): number => Math.round(px * 0.75);

// Build image style — if width is set (from editor resize), use it in points.
// If no width, use only marginVertical and let react-pdf render at natural size.
const getImageStyle = (widthPx?: number) => {
    const base: any = { marginVertical: 8, maxWidth: '100%' };
    if (typeof widthPx === 'number' && widthPx > 0) {
        base.width = pxToPt(widthPx);
    }
    return base;
};
// Register Pacifico font for engagement letters
Font.register({
    family: 'Pacifico',
    src: 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/Pacifico.ttf',
});

// PDF Styles - using built-in Helvetica font (no external font loading)
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 40,
        fontFamily: 'Helvetica',
        fontSize: 10,
        lineHeight: 1.5,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        marginBottom: 10,
    },
    logo: {
        maxWidth: 80,
        maxHeight: 40,
        objectFit: 'contain',
    },
    footer: {
        marginTop: 5,
        textAlign: 'left',
        fontSize: 20,
        color: '#999999',
        paddingTop: 8,
    },
    content: {
        flex: 1,
    },
    // Typography
    paragraph: {
        marginBottom: 4,
        fontSize: 10,
        lineHeight: 1.5,
    },
    headingOne: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 12,
        marginTop: 16,
    },
    headingTwo: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 10,
        marginTop: 14,
    },
    headingThree: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 4,
        marginTop: 8,
    },
    // Heading three inside table cells - more compact spacing
    headingThreeInCell: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 2,
        marginTop: 4,
        lineHeight: 1.5,
    },
    blockquote: {
        borderLeftWidth: 3,
        borderLeftColor: '#dddddd',
        paddingLeft: 12,
        marginVertical: 8,
        fontStyle: 'italic',
        color: '#666666',
    },
    // Lists
    orderedList: {
        marginBottom: 2,
        marginLeft: 16,
    },
    unorderedList: {
        marginBottom: 2,
        marginLeft: 16,
    },
    // Lists inside table cells - more compact, no left padding since cell has padding
    listInCell: {
        marginBottom: 0,
        paddingLeft: 0,
    },
    listItem: {
        flexDirection: 'row',
        marginBottom: 1,
        paddingLeft: 0,
    },
    listItemBullet: {
        width: 8,
        fontSize: 9,
        marginRight: 2,
        textAlign: 'right',
    },
    listItemContent: {
        flex: 1,
        fontSize: 9,
    },
    // Tables - each row is fully bordered to handle page breaks properly
    table: {
        width: '100%',
        marginVertical: 8,
    },
    tableRow: {
        flexDirection: 'row',
        minHeight: 20,
        borderWidth: 1,
        borderColor: '#d0d0d0',
        marginTop: -1, // Collapse adjacent borders to prevent double lines
    },
    tableRowFirst: {
        marginTop: 0, // First row doesn't need negative margin
    },
    tableRowLast: {
        // Last row same as others - has all borders
    },
    tableHeader: {
        backgroundColor: '#e8e8e8',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 9,
        borderRightWidth: 1,
        borderRightColor: '#d0d0d0',
        // flex: 1, - REMOVED: Conflicts with explicit width
        justifyContent: 'flex-start',
    },
    tableHeaderLast: {
        borderRightWidth: 0,
    },
    tableCell: {
        padding: 6,
        fontSize: 9,
        borderRightWidth: 1,
        borderRightColor: '#d0d0d0',
        // flex: 1, - REMOVED: Conflicts with explicit width
        justifyContent: 'flex-start',
    },
    tableCellLast: {
        borderRightWidth: 0,
    },
    // Text formatting
    bold: {
        fontWeight: 'bold',
    },
    italic: {
        fontStyle: 'italic',
    },
    underline: {
        textDecoration: 'underline',
    },
    strikethrough: {
        textDecoration: 'line-through',
    },
    link: {
        color: '#0066cc',
        textDecoration: 'underline',
    },
    // Image — no explicit width so it renders at natural size
    // maxWidth prevents overflow on large images
    image: {
        maxWidth: '100%',
        marginVertical: 8,
    },
    // Watermark (image from assets/watermark.svg) - covers entire page, behind content
    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.15, // Subtle watermark that won't cover content
    },
    // Text watermark fallback (when image watermark is not available)
    textWatermark: {
        position: 'absolute',
        top: '40%',
        left: '25%',
        fontSize: 120,
        color: '#cccccc',
        opacity: 0.15,
        transform: 'rotate(-30deg)',
    },
});

export interface ReactPdfDocumentProps {
    value: Descendant[];
    pageSize: 'A3' | 'A4';
    logoBase64?: string;
    orgName?: string;
    companyName?: string;
    includeWatermark?: boolean;
    showFooter?: boolean;
    documentType?: 'onePager' | 'engagementLetter' | 'bureauKycConsent' | 'nda' | 'applicationForm';
}

// Helper to render text with formatting including bgColor
const renderTextNode = (node: any, key: number): React.ReactNode => {
    if (!SlateText.isText(node)) return null;

    const text = node.text || '';
    if (!text) return null;

    const textStyle: any = {};

    if (node.bold) textStyle.fontWeight = 'bold';
    if (node.italic) textStyle.fontStyle = 'italic';
    const decorations: string[] = [];
    if (node.underline) decorations.push('underline');
    if (node.strikethrough) decorations.push('line-through');
    if (decorations.length) textStyle.textDecoration = decorations.join(' ');
    if (node.color) textStyle.color = node.color;
    if (node.fontSize) {
        // Convert CSS pixels to PDF points: 1px ≈ 0.75pt (at 96 DPI standard)
        // This ensures "14px" in editor renders closer to actual 14px size in PDF
        const pixelSize = parseInt(String(node.fontSize), 10) || 10;
        textStyle.fontSize = Math.round(pixelSize * 0.75);
    }
    if (node.fontFamily === 'pacifico') textStyle.fontFamily = 'Pacifico';
    if (node.superscript) {
        textStyle.fontSize = 7;
    }
    if (node.subscript) {
        textStyle.fontSize = 7;
    }

    // Handle background color - apply directly to Text for proper PDF rendering
    if (node.bgColor) {
        textStyle.backgroundColor = node.bgColor;
    }

    if (Object.keys(textStyle).length > 0) {
        return (
            <Text key={key} style={textStyle}>
                {text}
            </Text>
        );
    }
    return <Text key={key}>{text}</Text>;
};

// Recursively render block-level children (paragraph, list, text).
// Handles nested paragraph -> paragraph -> text and paragraph -> list -> list-item.
// Also handles mixed text nodes and elements at the same level.
const renderBlockChildren = (children: any[], textAlign?: string, keyPrefix = 0): React.ReactNode[] => {
    if (!children || children.length === 0) return [];
    const result: React.ReactNode[] = [];
    children.forEach((child, index) => {
        const key = keyPrefix * 1000 + index;
        if (SlateText.isText(child)) {
            // Text node directly - check if it has textAlign or other block properties
            const nodeTextAlign = (child as any).textAlign || textAlign;
            const textNode = renderTextNode(child, key);
            if (nodeTextAlign && nodeTextAlign !== textAlign) {
                // Wrap in View with textAlign if text node has its own textAlign
                result.push(
                    <View key={key} style={{ textAlign: nodeTextAlign as any }}>
                        {textNode}
                    </View>,
                );
            } else {
                result.push(textNode);
            }
        } else if (SlateElement.isElement(child)) {
            const childType = (child as any).type;
            if (childType === 'paragraph') {
                // Use paragraph's textAlign if present, otherwise use parent's
                const paraTextAlign = (child as any).textAlign || textAlign;
                const blockContent = renderBlockChildren((child as any).children || [], paraTextAlign, key);
                // Apply paragraph style for proper spacing
                // Use smaller marginBottom (2) in table cells to avoid excessive spacing with multiple paragraphs
                // Render even empty paragraphs to preserve spacing between content
                const paragraphStyle = paraTextAlign
                    ? [{ marginBottom: 2, fontSize: 10, lineHeight: 1.5 }, { textAlign: paraTextAlign as any }]
                    : { marginBottom: 2, fontSize: 10, lineHeight: 1.5 };
                result.push(
                    <View key={key} style={paragraphStyle}>
                        <Text>{blockContent.length > 0 ? blockContent : ' '}</Text>
                    </View>,
                );
            } else if (childType === 'headingThree') {
                // Handle headingThree in table cells - use compact cell style
                const headingTextAlign = (child as any).textAlign || textAlign;
                const headingContent = (child.children || [])
                    .map((c: any, i: number) => (SlateText.isText(c) ? renderTextNode(c, key * 100 + i) : null))
                    .filter(Boolean);
                if (headingContent.length > 0) {
                    result.push(
                        <View key={key} style={[styles.headingThreeInCell, { textAlign: headingTextAlign as any }]}>
                            <Text>{headingContent}</Text>
                        </View>,
                    );
                }
            } else if (childType === 'image') {
                const url = (child as any).url;
                if (url) {
                    result.push(<Image key={key} src={url} style={getImageStyle((child as any).width)} />);
                }
            } else if (childType === 'unorderedList' || childType === 'orderedList') {
                const isOrdered = childType === 'orderedList';
                const listItems = ((child as any).children || [])
                    .map((listItem: any, liIndex: number) => {
                        if ((listItem as any).type !== 'list-item') return null;
                        const bullet = isOrdered ? `${liIndex + 1}.` : '•';

                        // Separate text/paragraph content from nested lists
                        const textContentNodes: any[] = [];
                        const nestedListNodes: any[] = [];

                        // Extract textAlign from paragraph children if present
                        let itemTextAlign = textAlign; // Inherit from parent by default
                        (listItem.children || []).forEach((liChild: any, i: number) => {
                            if (SlateText.isText(liChild)) {
                                textContentNodes.push(<Text key={i}>{renderTextNode(liChild, i)}</Text>);
                            } else if (SlateElement.isElement(liChild)) {
                                const liChildType = (liChild as any).type;
                                if (liChildType === 'paragraph') {
                                    // Extract textAlign from paragraph
                                    const paraTextAlign = (liChild as any).textAlign;
                                    if (paraTextAlign) {
                                        itemTextAlign = paraTextAlign;
                                    }
                                    const paragraphContent = (liChild.children || [])
                                        .map((c: any, j: number) => {
                                            if (SlateText.isText(c)) {
                                                return renderTextNode(c, i * 100 + j);
                                            }
                                            return null;
                                        })
                                        .filter(Boolean);
                                    textContentNodes.push(<Text key={i}>{paragraphContent}</Text>);
                                } else if (liChildType === 'unorderedList' || liChildType === 'orderedList') {
                                    // Nested list - recursively render list items
                                    const nestedIsOrdered = liChildType === 'orderedList';
                                    const nestedItems = ((liChild as any).children || [])
                                        .map((nestedListItem: any, nliIndex: number) => {
                                            if ((nestedListItem as any).type !== 'list-item') return null;
                                            const nestedBullet = nestedIsOrdered ? `${nliIndex + 1}.` : '•';
                                            const nestedContent = renderBlockChildren(
                                                nestedListItem.children || [],
                                                textAlign,
                                                key + i * 100 + nliIndex,
                                            );
                                            return (
                                                <View key={key + i * 100 + nliIndex} style={styles.listItem}>
                                                    <Text style={styles.listItemBullet}>{nestedBullet}</Text>
                                                    <View style={styles.listItemContent}>
                                                        {nestedContent.length > 0 ? nestedContent : <Text> </Text>}
                                                    </View>
                                                </View>
                                            );
                                        })
                                        .filter(Boolean);
                                    if (nestedItems.length > 0) {
                                        nestedListNodes.push(
                                            <View key={key + i} style={{ marginLeft: 16, marginTop: 2 }}>
                                                {nestedItems}
                                            </View>,
                                        );
                                    }
                                }
                            }
                        });

                        const contentStyle = itemTextAlign
                            ? [styles.listItemContent, { textAlign: itemTextAlign as any }]
                            : styles.listItemContent;

                        return (
                            <View key={key + liIndex}>
                                <View style={styles.listItem}>
                                    <Text style={styles.listItemBullet}>{bullet}</Text>
                                    <View style={contentStyle}>{textContentNodes.length > 0 ? textContentNodes : <Text> </Text>}</View>
                                </View>
                                {nestedListNodes.length > 0 && <View>{nestedListNodes}</View>}
                            </View>
                        );
                    })
                    .filter(Boolean);
                result.push(
                    <View key={key} style={styles.listInCell}>
                        {listItems}
                    </View>,
                );
            }
        }
    });
    return result;
};

// Render cell content - handles paragraphs, lists, and nested structures
const renderCellContent = (children: any[], textAlign?: string): React.ReactNode => {
    if (!children || children.length === 0) return <Text> </Text>;
    const result = renderBlockChildren(children, textAlign, 0);
    if (result.length === 0) return <Text> </Text>;
    return result;
};

// Per-table column-width overrides for tables whose default 30% / equal-share layout
// produces poor results (e.g. unwrappable long numbers in narrow columns).
// Each array MUST sum to 100 and have the same length as the column count.
const TABLE_COLUMN_WIDTH_OVERRIDES: Record<string, number[]> = {
    // Banking Summary (7 cols): explicit widths to fit long account numbers without
    // auto-shrink. Fixed: Bank Name 28%, Account No. 15%. Remaining 5 columns share
    // the leftover 57% equally → 11.4% each.
    // Bank Name | Account No. | Closing Balance | Closing Balance Date | OD/CC Sanction Limit | Transactions (LTM) | Missing Days (LTM)
    table_bankingSummary: [28, 15, 11.4, 11.4, 11.4, 11.4, 11.4],
};

// Helper to render a table row with proper cell styling
const renderTableRow = (
    row: any,
    rowIndex: number,
    isLastRow: boolean,
    isTableWithoutHeaders: boolean,
    tableKey?: string,
): React.ReactNode => {
    if (!row || !row.children) return null;

    const isFirstRow = rowIndex === 0;
    const rowStyle: any[] = [styles.tableRow, ...(isFirstRow ? [styles.tableRowFirst] : []), ...(isLastRow ? [styles.tableRowLast] : [])];

    const totalColumns = row.children.length;
    const overrides = tableKey ? TABLE_COLUMN_WIDTH_OVERRIDES[tableKey] : undefined;
    const useOverrides = overrides && overrides.length === totalColumns;

    return (
        <View key={rowIndex} style={rowStyle} minPresenceAhead={20}>
            {row.children.map((cell: any, cellIndex: number) => {
                const isLastCell = cellIndex === row.children.length - 1;
                const cellType = (cell as any).type;

                // For tableWithoutHeaders, always use regular cell styling (no gray bg, no bold)
                const isHeader = !isTableWithoutHeaders && cellType === 'table-header';

                // Use cell's textAlign if explicitly set, otherwise default to left (match editor)
                const textAlign = (cell as any).textAlign;

                const cellContent = renderCellContent(cell.children || [], textAlign);

                // Calculate column width: per-table overrides if defined; otherwise
                // legacy CSS-mirroring rule (first col 30%, rest share 70%).
                let columnWidth: string;
                if (useOverrides) {
                    columnWidth = `${overrides![cellIndex]}%`;
                } else if (cellIndex === 0) {
                    columnWidth = '30%';
                } else {
                    const remainingColumns = totalColumns - 1;
                    const widthPerColumn = 70 / remainingColumns;
                    columnWidth = `${widthPerColumn.toFixed(2)}%`;
                }

                // Determine cell style based on header/cell and last position
                const baseStyle = isHeader ? styles.tableHeader : styles.tableCell;
                const lastStyle = isHeader ? styles.tableHeaderLast : styles.tableCellLast;

                // Apply width to cell and ensure minimum height for empty cells
                const cellStyleWithWidth = [
                    baseStyle,
                    { width: columnWidth, minHeight: 20 }, // minHeight ensures empty cells maintain proper height
                    textAlign ? { textAlign: textAlign as any } : {},
                ];

                const finalStyle = isLastCell ? [...cellStyleWithWidth, lastStyle] : cellStyleWithWidth;

                return (
                    <View key={cellIndex} style={finalStyle}>
                        {cellContent}
                    </View>
                );
            })}
        </View>
    );
};

// Recursive element renderer
const renderElement = (node: any, key: number, listIndex?: number): React.ReactNode => {
    if (SlateText.isText(node)) {
        return renderTextNode(node, key);
    }

    if (!SlateElement.isElement(node)) {
        return null;
    }

    const nodeType = (node as any).type;
    const textAlign = (node as any).textAlign || 'left';

    switch (nodeType) {
        case 'paragraph': {
            const children = (node.children || []).map((child: any, i: number) =>
                SlateText.isText(child) ? renderTextNode(child, i) : renderElement(child, i),
            );
            return (
                <View key={key} style={[styles.paragraph, { textAlign: textAlign as any }]}>
                    <Text>{children.length > 0 ? children : ' '}</Text>
                </View>
            );
        }

        case 'headingOne': {
            const children = (node.children || [])
                .map((child: any, i: number) => (SlateText.isText(child) ? renderTextNode(child, i) : null))
                .filter(Boolean);
            return (
                <View key={key} style={[styles.headingOne, { textAlign: textAlign as any }]} minPresenceAhead={50}>
                    <Text>{children}</Text>
                </View>
            );
        }

        case 'headingTwo': {
            const children = (node.children || [])
                .map((child: any, i: number) => (SlateText.isText(child) ? renderTextNode(child, i) : null))
                .filter(Boolean);
            return (
                <View key={key} style={[styles.headingTwo, { textAlign: textAlign as any }]} minPresenceAhead={40}>
                    <Text>{children}</Text>
                </View>
            );
        }

        case 'headingThree': {
            const children = (node.children || [])
                .map((child: any, i: number) => (SlateText.isText(child) ? renderTextNode(child, i) : null))
                .filter(Boolean);
            return (
                <View key={key} style={[styles.headingThree, { textAlign: textAlign as any }]} minPresenceAhead={30}>
                    <Text>{children}</Text>
                </View>
            );
        }

        case 'blockquote': {
            const children = (node.children || []).map((child: any, i: number) =>
                SlateText.isText(child) ? renderTextNode(child, i) : renderElement(child, i),
            );
            return (
                <View key={key} style={styles.blockquote}>
                    <Text>{children}</Text>
                </View>
            );
        }

        case 'orderedList':
            return (
                <View key={key} style={styles.orderedList}>
                    {(node.children || []).map((child: any, i: number) => renderElement(child, i, i + 1))}
                </View>
            );

        case 'unorderedList':
            return (
                <View key={key} style={styles.unorderedList}>
                    {(node.children || []).map((child: any, i: number) => renderElement(child, i, -1))}
                </View>
            );

        case 'list-item': {
            const bullet = listIndex && listIndex > 0 ? `${listIndex}.` : '•';

            // Extract textAlign from list-item node or its paragraph children
            let itemTextAlign = textAlign; // Inherit from parent by default
            if ((node as any).textAlign) {
                itemTextAlign = (node as any).textAlign;
            }

            // Separate text/paragraph content from nested lists
            const textContent: any[] = [];
            const nestedLists: any[] = [];

            (node.children || []).forEach((child: any, i: number) => {
                if (SlateText.isText(child)) {
                    textContent.push(<Text key={i}>{renderTextNode(child, i)}</Text>);
                } else if (SlateElement.isElement(child)) {
                    const childType = (child as any).type;
                    if (childType === 'paragraph') {
                        // Extract textAlign from paragraph if present
                        const paraTextAlign = (child as any).textAlign;
                        if (paraTextAlign) {
                            itemTextAlign = paraTextAlign;
                        }
                        const paragraphContent = (child.children || [])
                            .map((c: any, j: number) => {
                                if (SlateText.isText(c)) {
                                    return renderTextNode(c, i * 100 + j);
                                }
                                return null;
                            })
                            .filter(Boolean);
                        textContent.push(<Text key={i}>{paragraphContent}</Text>);
                    } else if (childType === 'orderedList' || childType === 'unorderedList') {
                        // Nested list - render it recursively
                        nestedLists.push(renderElement(child, i, childType === 'orderedList' ? 1 : -1));
                    }
                }
            });

            const contentStyle = itemTextAlign ? [styles.listItemContent, { textAlign: itemTextAlign as any }] : styles.listItemContent;

            return (
                <View key={key} wrap={false}>
                    <View style={styles.listItem}>
                        <Text style={styles.listItemBullet}>{bullet}</Text>
                        <View style={contentStyle}>{textContent.length > 0 ? textContent : <Text> </Text>}</View>
                    </View>
                    {nestedLists.length > 0 && <View>{nestedLists}</View>}
                </View>
            );
        }

        case 'table':
        case 'tableWithoutHeaders': {
            const isTableWithoutHeaders = nodeType === 'tableWithoutHeaders';
            const tableKey = (node as any).key as string | undefined;

            return (
                <View key={key} style={styles.table}>
                    {(node.children || []).map((row: any, rowIndex: number) => {
                        const isLastRow = rowIndex === (node.children?.length || 0) - 1;
                        return renderTableRow(row, rowIndex, isLastRow, isTableWithoutHeaders, tableKey);
                    })}
                </View>
            );
        }

        case 'table-row':
            // This is handled by the table case above
            return null;

        case 'table-header': {
            const headerContent = renderCellContent(node.children || []);
            return (
                <View key={key} style={styles.tableHeader}>
                    {headerContent}
                </View>
            );
        }

        case 'table-cell': {
            const cellContent = renderCellContent(node.children || []);
            return (
                <View key={key} style={styles.tableCell}>
                    {cellContent}
                </View>
            );
        }

        case 'link': {
            const href = (node as any).url || (node as any).href || '#';
            const children = (node.children || [])
                .map((child: any, i: number) => (SlateText.isText(child) ? renderTextNode(child, i) : null))
                .filter(Boolean);
            return (
                <Link key={key} src={href} style={styles.link}>
                    <Text>{children}</Text>
                </Link>
            );
        }

        case 'image': {
            const url = (node as any).url;
            if (url) {
                return <Image key={key} src={url} style={getImageStyle((node as any).width)} />;
            }
            return null;
        }

        default: {
            // Default to paragraph-like rendering
            const children = (node.children || []).map((child: any, i: number) =>
                SlateText.isText(child) ? renderTextNode(child, i) : renderElement(child, i),
            );
            return (
                <View key={key} style={styles.paragraph}>
                    <Text>{children}</Text>
                </View>
            );
        }
    }
};

/**
 * ReactPdfDocument Component
 *
 * Converts Slate editor content to a PDF document using @react-pdf/renderer.
 * Handles all Slate element types including tables, lists, and text formatting.
 */
const ReactPdfDocument: React.FC<ReactPdfDocumentProps> = ({
    value,
    pageSize,
    logoBase64,
    orgName,
    companyName,
    includeWatermark,
    showFooter = true,
    documentType = 'onePager',
}) => {
    // Safely render content with error boundary
    const renderContent = () => {
        if (!value || !Array.isArray(value) || value.length === 0) {
            return <Text>No content to display</Text>;
        }

        try {
            const rendered = value.map((node, index) => {
                try {
                    return renderElement(node, index);
                } catch (nodeErr) {
                    console.error('Error rendering node');
                    return <Text key={index}>Error rendering element</Text>;
                }
            });
            return rendered;
        } catch (err) {
            console.error('Error rendering PDF content');
            return <Text>Error rendering content</Text>;
        }
    };

    // Generate document title based on type
    const getDocumentTitle = () => {
        const titleMap = {
            onePager: 'One Pager',
            engagementLetter: 'Engagement Letter',
            bureauKycConsent: 'Bureau KYC Consent',
            nda: 'NDA',
        };
        const suffix = titleMap[documentType] || 'Document';
        return companyName ? `${companyName} ${suffix}` : suffix;
    };

    return (
        <Document title={getDocumentTitle()} author={orgName || 'AICA'} creator="AICA Platform">
            <Page size={pageSize} style={styles.page} wrap>
                {/* Watermark - render FIRST so it's in the background layer */}
                {includeWatermark &&
                    (watermarkForOnePager ? (
                        <Image src={watermarkForOnePager} style={styles.watermark} fixed cache={false} />
                    ) : (
                        <Text style={styles.textWatermark} fixed>
                            AICA
                        </Text>
                    ))}

                {/* Header with logo - only on first page */}
                {logoBase64 && logoBase64.length > 0 ? (
                    <View style={styles.header} fixed>
                        <Image src={logoBase64} style={styles.logo} cache={false} />
                    </View>
                ) : (
                    orgName && (
                        <View style={styles.header} fixed>
                            <Text style={{ fontSize: 16, fontWeight: 'bold' }}>{orgName}</Text>
                        </View>
                    )
                )}

                {/* Content */}
                <View style={styles.content}>
                    {renderContent()}

                    {/* Footer - appears at end of content (last page only) */}
                    {showFooter && (
                        <View style={styles.footer}>
                            <Text>Built on AICA by Recur Club</Text>
                        </View>
                    )}
                </View>
            </Page>
        </Document>
    );
};

export default ReactPdfDocument;
